import { EntityMetadata, EntityDiscoveryResult, EntitiesByApplicationResult } from './types/entity-discovery.types';
import { getBaseUrl } from '../framework/config/api.config';

export class EntityDiscoveryService {
  private cache: Map<string, any> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  constructor() {
    // No need to store baseUrl as it's dynamically resolved
  }

  private getBaseUrl(): string {
    return getBaseUrl().replace(/\/$/, ''); // Remove trailing slash
  }

  /**
   * Discover all available entities
   */
  async discoverEntities(forceRefresh = false): Promise<EntityDiscoveryResult> {
    const cacheKey = 'entities';
    
    if (!forceRefresh && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    const url = `${this.getBaseUrl()}/entity-discovery${forceRefresh ? '?refresh=true' : ''}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to discover entities: ${response.statusText}`);
      }

      const data: EntityDiscoveryResult = await response.json();
      
      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('Error discovering entities:', error);
      throw error;
    }
  }

  /**
   * Get metadata for a specific entity
   */
  async getEntityMetadata(entityName: string): Promise<EntityMetadata> {
    const cacheKey = `entity-${entityName}`;
    
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    const url = `${this.getBaseUrl()}/entity-discovery/entities/${entityName}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Entity '${entityName}' not found`);
        }
        throw new Error(`Failed to get entity metadata: ${response.statusText}`);
      }

      const data: EntityMetadata = await response.json();
      
      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error(`Error getting metadata for entity ${entityName}:`, error);
      throw error;
    }
  }

  /**
   * Get JSON schema for a specific entity
   */
  async getEntityJsonSchema(entityName: string): Promise<any> {
    const cacheKey = `schema-${entityName}`;
    
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    const url = `${this.getBaseUrl()}/entity-discovery/entities/${entityName}/schema`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Entity '${entityName}' not found`);
        }
        throw new Error(`Failed to get entity schema: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error(`Error getting schema for entity ${entityName}:`, error);
      throw error;
    }
  }

  /**
   * Get list of entity names
   */
  async getEntityNames(): Promise<string[]> {
    const cacheKey = 'entity-names';
    
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    const url = `${this.getBaseUrl()}/entity-discovery/entities`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to get entity names: ${response.statusText}`);
      }

      const result = await response.json();
      const entityNames = result.entities;
      
      // Cache the result
      this.cache.set(cacheKey, {
        data: entityNames,
        timestamp: Date.now()
      });

      return entityNames;
    } catch (error) {
      console.error('Error getting entity names:', error);
      throw error;
    }
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get entities grouped by application
   */
  async getEntitiesByApplication(): Promise<EntitiesByApplicationResult> {
    const cacheKey = 'entities-by-application';
    
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    const url = `${this.getBaseUrl()}/entity-discovery/entities/by-application`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to get entities by application: ${response.statusText}`);
      }

      const data: EntitiesByApplicationResult = await response.json();
      
      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('Error getting entities by application:', error);
      throw error;
    }
  }

  /**
   * Refresh the discovery cache on the server
   */
  async refreshServerCache(): Promise<void> {
    const url = `${this.getBaseUrl()}/entity-discovery/refresh-cache`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to refresh server cache: ${response.statusText}`);
      }
      
      // Clear our local cache as well
      this.clearCache();
    } catch (error) {
      console.error('Error refreshing server cache:', error);
      throw error;
    }
  }
}

// Create a singleton instance
export const entityDiscoveryService = new EntityDiscoveryService();
